import { fromPromise } from "xstate";
import type { DeletionEvent } from "../state-machines/deletion-machine";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { createListMutation, updateListMutation } from "./mutations";

export interface ListActions {
  createList: (
    name: string,
    opts?: {
      color?: string;
      icon?: string;
      groupId?: string;
      themeId?: string | null;
      isBlur?: boolean;
    },
  ) => Promise<string | undefined>;
  updateList: (
    id: string,
    updates: Partial<{
      name: string;
      color: string | null;
      icon: string | null;
      theme_id: string | null;
      is_blur: number;
    }>,
  ) => Promise<void>;
  createGroup: (name: string) => Promise<string | undefined>;
  updateGroup: (id: string, updates: Partial<{ name: string }>) => Promise<void>;
  assignListToGroup: (listId: string, groupId: string | null) => Promise<void>;
}

export function createListHandlers(
  sendDeletion: (event: DeletionEvent) => void,
  actions: ListActions,
) {
  return {
    handleCreateList: async (name: string, opts?: Parameters<ListActions["createList"]>[1]) =>
      actions.createList(name, opts),
    handleUpdateList: async (id: string, updates: Parameters<ListActions["updateList"]>[1]) =>
      actions.updateList(id, updates),
    handleDeleteList: (id: string) => {
      sendDeletion({ type: "DELETE", itemId: id, itemType: "list" });
    },
    handleCreateGroup: async (name: string) => actions.createGroup(name),
    handleUpdateGroup: async (id: string, updates: Parameters<ListActions["updateGroup"]>[1]) =>
      actions.updateGroup(id, updates),
    handleDeleteGroup: (id: string) => {
      sendDeletion({ type: "DELETE", itemId: id, itemType: "group" });
    },
    handleAssignListToGroup: async (listId: string, groupId: string | null) =>
      actions.assignListToGroup(listId, groupId),
  };
}

/**
 * Provides standard actor implementations for listFormMachine.
 * Unifies logic between native and web apps.
 */
export function getListFormActors(options: {
  db: PowerSyncDrizzleDB | null;
  userId: string | undefined;
}) {
  const { db, userId } = options;

  return {
    saveListActor: fromPromise<
      // oxlint-disable-next-line typescript-eslint/no-invalid-void-type
      void,
      {
        id?: string;
        groupId?: string;
        values: {
          name: string;
          color: string;
          icon: string;
          themeId: string | null;
          isBlur: boolean;
        };
      }
    >(async ({ input }) => {
      if (!db) {
        throw new Error("Database not initialized");
      }

      const { id, groupId, values } = input;

      if (id) {
        await updateListMutation(db, id, {
          name: values.name.trim(),
          color: values.color,
          icon: values.icon,
          theme_id: values.themeId,
          is_blur: values.isBlur ? 1 : 0,
        });
      } else {
        if (!userId) {
          throw new Error("User not authenticated");
        }
        await createListMutation(db, userId, values.name.trim(), {
          color: values.color,
          icon: values.icon,
          groupId,
          themeId: values.themeId,
          isBlur: values.isBlur,
        });
      }
    }),

    // oxlint-disable-next-line typescript-eslint/no-invalid-void-type
    updateThemePreviewActor: fromPromise<void, { id: string; themeId: string | null }>(
      async ({ input }) => {
        if (!db || !input.id) {
          return;
        }
        await updateListMutation(db, input.id, { theme_id: input.themeId });
      },
    ),
  };
}
